import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { EventWordmark } from '../EventWordmark';
import { velarisClosing } from '../../data/velaris';
import { contact, legalLinks, socialProfiles } from '../../data/contact';
const ease = [0.22, 1, 0.36, 1] as const;
const footLinks = [{
  label: 'Superluxury',
  to: '/'
}, {
  label: 'Membership',
  to: '/membership'
}, {
  label: 'Private Line',
  to: '/private-line'
}, {
  label: 'Privacy',
  to: legalLinks.privacyPolicy
}, {
  label: 'Imprint',
  to: legalLinks.legalNotice
}];

// The final frame: a full-bleed night scene, one invitation, and a single quiet line of links
export function VelarisClosing() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end end']
  });
  const scale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.2, 1]);
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-8%', '0%']);
  const reveal = (delay: number) => ({
    initial: {
      opacity: 0,
      y: reduce ? 0 : 24,
      filter: reduce ? 'blur(0px)' : 'blur(10px)'
    },
    whileInView: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)'
    },
    viewport: {
      once: true,
      amount: 0.5
    },
    transition: {
      duration: 1.4,
      ease,
      delay
    }
  });
  return <section ref={ref} aria-labelledby="velaris-closing" className="relative flex min-h-screen flex-col overflow-hidden text-paper" data-id="element-1202" data-mp="33cesj8q121km">
      <motion.img src={velarisClosing.image} alt={velarisClosing.alt} style={{
      scale,
      y
    }} className="absolute inset-0 h-full w-full object-cover" data-id="element-1203" data-mp="3d90124199ut7" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.35),rgba(0,0,0,0.75))]" aria-hidden="true" data-id="element-1204" data-mp="3i1jf123uuryj" />
      <div className="absolute inset-x-0 top-0 h-48 bg-[linear-gradient(to_bottom,rgb(10,10,10),rgba(10,10,10,0))]" aria-hidden="true" data-id="element-1205" data-mp="2pivvhr4ibkzk" />
      <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.07] motion-reduce:hidden" aria-hidden="true" data-id="element-1206" data-mp="2hh6itoav60nu" />

      <div className="relative flex flex-1 items-center justify-center px-6 py-28 text-center md:px-16" data-id="element-1207" data-mp="2r8j9wdou8mst">
        <div className="max-w-[920px]" data-id="element-1208" data-mp="2m9g1cj9nfyg3">
          <motion.div {...reveal(0)} aria-hidden="true" data-id="element-1209" data-mp="2mahin5h9dh69">
            <EventWordmark name="Venturist" className="text-[76px] text-paper md:text-[124px]" data-id="element-1210" data-mp="1yjvg1tq0wp3n" />
          </motion.div>

          <motion.h2 {...reveal(0.2)} id="velaris-closing" className="mt-6 font-editorial text-[36px] leading-[42px] tracking-[-0.015em] [text-shadow:0_2px_40px_rgba(0,0,0,0.5)] md:text-[56px] md:leading-[62px]" data-id="element-1211" data-mp="1uis77r6e7tbx">
            {velarisClosing.line}
          </motion.h2>

          <motion.dl {...reveal(0.4)} className="mx-auto mt-10 flex max-w-[560px] justify-center gap-10 border-y border-paper/25 py-5 md:gap-16" data-id="element-1212" data-mp="3jx821n7x1hsf">
            {velarisClosing.details.map(d => <div key={d.label} data-id="element-1213" data-mp="3f0hpvaz8f6ca">
                <dt className="text-[12px] font-light text-paper/70" data-id="element-1214" data-mp="10q2w2ew2pq4e">{d.label}</dt>
                <dd className="mt-1 text-[15px]" data-id="element-1215" data-mp="15p73azzftds3">{d.value}</dd>
              </div>)}
          </motion.dl>

          <motion.div {...reveal(0.6)} className="mt-12 flex flex-col items-center gap-4" data-id="element-1216" data-mp="rqfrxlbym67n">
            <Link to="/membership#application" className="group inline-flex items-center gap-3 bg-paper px-10 py-5 text-[17px] font-medium text-ink transition-[background-color,transform] duration-300 ease-luxe hover:-translate-y-0.5 hover:bg-paper/90" data-id="element-1217" data-mp="wnz8irw4qv2r">
              <LockIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-1218" data-mp="1ap1tt2faw1cm" />
              Request your invitation
              <span aria-hidden="true" className="transition-transform duration-500 ease-luxe group-hover:translate-x-1" data-id="element-1219" data-mp="1fmbuxfc1y1ga">
                →
              </span>
            </Link>
            <span className="text-[13px] font-light text-paper/75" data-id="element-1220" data-mp="28460p5e0xinm">Extended to members, and by introduction.</span>
          </motion.div>
        </div>
      </div>

      {/* Single-line footer */}
      <footer className="relative border-t border-paper/15 px-6 py-4 md:px-16" data-id="element-1221" data-mp="eiicbgo6lyxa">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] font-light text-paper/60 md:justify-between" data-id="element-1222" data-mp="aheuiomkpstf">
          <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-id="element-1223" data-mp="5k6bymxijmrn">
            {footLinks.map(l => <Link key={l.label} to={l.to} className="transition-colors duration-150 hover:text-paper" data-id="element-1224" data-mp="y6hcxyp17poz">
                {l.label}
              </Link>)}
            <a href={socialProfiles[0].href} target="_blank" rel="noopener noreferrer" className="transition-colors duration-150 hover:text-paper" data-id="element-1225" data-mp="te6g54pl8k0n">
              Instagram
            </a>
          </nav>
          <span data-id="element-1226" data-mp="1lyep5qwe8jqm">© 2026 Superluxury · {contact.domain}</span>
        </div>
      </footer>
    </section>;
}